import { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Box,
    Autocomplete, TextField, CircularProgress, IconButton, Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { toast } from "react-toastify";
import api, { apiError } from "../api";

/*
 * Which model answers every plant.
 *
 * This screen replaces the same dialog on FDMRAGPORTAL: the relay is becoming a thing
 * that only answers questions, and everything a person decides moves to this console.
 *
 * Three fixed slots rather than a list you add rows to. The chain is never longer than
 * three in practice, and naming the slots - first choice, if that fails, last resort -
 * says what the order means. A bare list of three dropdowns does not, and the order is
 * the entire meaning of this setting.
 */

const SLOTS = [
    { n: 1, label: "First choice", hint: "Every question goes here." },
    { n: 2, label: "If that fails", hint: "Only when the first cannot answer at all." },
    { n: 3, label: "Last resort", hint: "When neither of the two above answered." },
];

const darkField = {
    "& .MuiOutlinedInput-root": {
        color: "#fff", borderRadius: 2,
        bgcolor: "rgba(255,255,255,.04)",
        "& fieldset": { borderColor: "rgba(255,255,255,.16)" },
        "&:hover fieldset": { borderColor: "rgba(255,255,255,.32)" },
        "&.Mui-focused fieldset": { borderColor: "#7BD95F" },
    },
    "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
        color: "rgba(255,255,255,.55)",
    },
    "& .MuiAutocomplete-input": { cursor: "pointer" },
};

const paper = {
    sx: {
        bgcolor: "#0C1A12", color: "#fff", borderRadius: 3,
        border: "1px solid rgba(255,255,255,.12)", backgroundImage: "none",
    },
};

const money = (m) => (m.free
    ? "free"
    : `$${m.promptPerM.toFixed(2)} / $${m.completionPerM.toFixed(2)} per 1M`);

const ctx = (m) => (m.contextLength ? `${Math.round(m.contextLength / 1000)}K ctx · ` : "");

export default function LlmSettings({ open, onClose }) {
    const [data, setData] = useState(null);
    const [chain, setChain] = useState(["", "", ""]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setData(null);
        api.get("/console/llm")
            .then(({ data }) => {
                setData(data);
                const c = data.chain || [];
                setChain([c[0] || "", c[1] || "", c[2] || ""]);
            })
            .catch((e) => { toast.error(apiError(e)); setData({ failed: true }); });
    }, [open]);

    const save = async () => {
        setBusy(true);
        try {
            // Blanks dropped, order kept. A hole in the middle is not a gap in the chain -
            // the relay only ever walks it top to bottom.
            await api.post("/console/llm", { models: chain.filter(Boolean) });
            toast.success("Model saved - live on the next question");
            onClose();
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    const models = data?.models || [];
    /*
     * The catalogue, plus whatever is currently saved.
     *
     * A model that has since been withdrawn from OpenRouter is still the model answering
     * every question here, and a strict dropdown would otherwise treat it as an invalid
     * value and quietly drop it - hiding exactly the thing somebody needs to see and fix.
     */
    const options = [...new Set([...models.map((m) => m.id), ...chain.filter(Boolean)])];
    const byId = (id) => models.find((m) => m.id === id);
    const nameOf = (id) => byId(id)?.name || id || "";

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth slotProps={{ paper }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" sx={{ alignItems: "flex-start", justifyContent: "space-between" }}>
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: 21 }}>AI Model</Typography>
                        <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13.5, mt: .3 }}>
                            Used by every licensed plant. Takes effect on the next question - no restart.
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,.6)", mt: -.5 }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <Divider sx={{ borderColor: "rgba(255,255,255,.1)" }} />

            <DialogContent>
                {!data ? (
                    <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress size={26} /></Box>
                ) : data.failed ? (
                    <Typography sx={{ color: "rgba(255,255,255,.6)" }}>
                        The model setting could not be read.
                    </Typography>
                ) : (
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        {data.catalogueError && (
                            <Typography sx={{ color: "#F3C969", fontSize: 13 }}>
                                The model catalogue is unreachable ({data.catalogueError}) - you can
                                still type an id by hand.
                            </Typography>
                        )}

                        {SLOTS.map((s, i) => {
                            const filled = Boolean(chain[i]);
                            return (
                                <Box key={s.n}>
                                    <Stack direction="row" spacing={1.2} sx={{ alignItems: "center", mb: 1 }}>
                                        <Box sx={{
                                            width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                                            display: "grid", placeItems: "center",
                                            fontSize: 12.5, fontWeight: 700,
                                            bgcolor: filled ? "#2E7D20" : "transparent",
                                            color: filled ? "#fff" : "rgba(255,255,255,.5)",
                                            border: filled ? "none" : "1px solid rgba(255,255,255,.25)",
                                        }}>{s.n}</Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: 14.5 }}>{s.label}</Typography>
                                        <Typography sx={{ color: "rgba(255,255,255,.45)", fontSize: 13 }}>
                                            {s.hint}
                                        </Typography>
                                    </Stack>

                                    <Autocomplete
                                        fullWidth
                                        /*
                                         * A dropdown that can be typed into, not a text box.
                                         *
                                         * No freeSolo: typing only filters the 400 entries, and
                                         * whatever is typed can never become the value. It used
                                         * to be allowed, and that meant a search that matched
                                         * nothing left the field holding text that was not a
                                         * model - saved, it would have ended every plant's next
                                         * question. Now a search with no match simply leaves the
                                         * current model in place.
                                         */
                                        openOnFocus
                                        autoHighlight
                                        selectOnFocus
                                        handleHomeEndKeys
                                        forcePopupIcon
                                        popupIcon={<ArrowDropDownIcon />}
                                        noOptionsText="No model matches that"
                                        isOptionEqualToValue={(o, v) => o === v}
                                        options={options}
                                        getOptionLabel={nameOf}
                                        value={chain[i] || ""}
                                        onChange={(e, v) => {
                                            const next = [...chain]; next[i] = v || ""; setChain(next);
                                        }}
                                        sx={darkField}
                                        slotProps={{
                                            paper: {
                                                sx: {
                                                    bgcolor: "#0C1A12", color: "#fff",
                                                    border: "1px solid rgba(255,255,255,.14)",
                                                    // The menu is its own layer outside the dialog,
                                                    // so it does not inherit any of its colours -
                                                    // left alone, the "no match" line renders in
                                                    // the theme's dark ink on this dark paper and
                                                    // reads as an empty box.
                                                    "& .MuiAutocomplete-noOptions": {
                                                        color: "rgba(255,255,255,.6)", fontSize: 14,
                                                    },
                                                    "& .MuiAutocomplete-option.Mui-focused": {
                                                        bgcolor: "rgba(255,255,255,.08)",
                                                    },
                                                    "& .MuiAutocomplete-option[aria-selected='true']": {
                                                        bgcolor: "rgba(46,125,32,.28)",
                                                    },
                                                },
                                            },
                                        }}
                                        /* Name on top, what it costs underneath. Choosing a model
                                           is choosing a price per question, and an id alone hides
                                           a fifty-fold difference between two similar names. */
                                        renderOption={(props, id) => {
                                            const m = byId(id);
                                            const { key, ...rest } = props;
                                            return (
                                                <Box component="li" key={key} {...rest}
                                                    sx={{ display: "block !important", py: 1 }}>
                                                    <Typography sx={{ fontSize: 14 }}>{m?.name || id}</Typography>
                                                    {m && (
                                                        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,.45)" }}>
                                                            {ctx(m)}{money(m)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        }}
                                        renderInput={(p) => (
                                            <TextField {...p} size="small"
                                                placeholder={i === 0 ? "Pick a model" : "Leave empty for none"} />
                                        )}
                                    />
                                </Box>
                            );
                        })}

                        <Divider sx={{ borderColor: "rgba(255,255,255,.1)" }} />

                        {/* The one thing people get wrong about a fallback chain. */}
                        <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: 13.5, lineHeight: 1.7 }}>
                            The next model is tried only when the one before it returned no answer at
                            all - rate limited, out of credit, blocked, or the provider was down. A
                            weak or wrong answer is still an answer, and the chain does not step past it.
                        </Typography>

                        <Typography sx={{ color: "rgba(255,255,255,.4)", fontSize: 12.5 }}>
                            {models.length} models listed - only those that support tools, since the
                            assistant cannot work without them.
                            {data.updatedAt
                                ? ` · Last changed ${new Date(data.updatedAt).toLocaleString("en-IN", { hour12: false })}.`
                                : ""}
                        </Typography>
                    </Stack>
                )}
            </DialogContent>

            <Divider sx={{ borderColor: "rgba(255,255,255,.1)" }} />

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} sx={{ color: "rgba(255,255,255,.65)" }}>Cancel</Button>
                <Button variant="contained" onClick={save}
                    disabled={busy || !data || data.failed || !chain[0]}
                    sx={{
                        borderRadius: 999, px: 3,
                    }}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}
